import * as d3 from "d3";
import _ from "lodash";
import $ from "jquery";
import moment from "moment";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Card, Divider, Dropdown, Header, Icon, Input, Label, Message, Popup, Segment, Select } from "semantic-ui-react";
import { hostAbsoluteUrl, restRequest } from "../../../http";
import { PlotChannelColors } from "../../../models/common_model";
import { DATA_TYPE_ARRAY, DATA_TYPE_SCALAR, DATA_TYPE_VECTOR, ParameteOverTime } from "../../../models/testing_model";
import { formatDateTime, toUrlQueryString } from "../../../utils";

const statusColors = ['', 'blue', 'red'];

export function OverTimePlot(props: { match: any }) {
    const id = props.match.params.id as string;
    const params = JSON.parse(decodeURIComponent(window.atob(props.match.params.params))) as any;
    const { measurement_name, export_file_name, measurement_key } = params;
    const maxChannel = params.maxChannel - 0;
    const dataType = params.dataType - 0;
    const productName = params.productName;
    const searchParams = _.omit(params, 'measurementKey', 'maxChannel', 'dataType', 'productName');

    const [selectedChannel, setSelectedChannel] = useState<number>(1);
    const [selectMean, setSelectMean] = useState(false);
    const [selectMedian, setSelectMedian] = useState(false);
    const [selectSigma3Limits, setSelectSigma3Limits] = useState(false);
    const [dataPreparing, setDataPreparing] = useState(true);
    const [canvasPreparing, setCanvasPreparing] = useState(true);

    const [serialNumberKeyword, setSerialNumberKeyword] = useState('');
    const [scatterData, setScatterData] = useState<ParameteOverTime[]>();
    const [selectedMouseY, setSelectedMouseY] = useState<number>(0);
    const [selectedData, setSelectedData] = useState<ParameteOverTime[]>([]);
    const [forSelectData, setForSelectData] = useState<ParameteOverTime[]>([]);
    const [forSelectSelectedData, setForSelectSelectedData] = useState<ParameteOverTime[]>([]);
    const [searchedData, setSearchedData] = useState<ParameteOverTime[]>([]);
    const [statisticsData, setStatisticsData] = useState<{ mean: number, median: number, std: number }>();
    const [totalNumber, setTotalNumber] = useState(0);
    const svgRootRef = useRef<HTMLDivElement>(null);
    const [canvasSize, setCanvasSize] = useState([0, 0]);
    const canvasContextRef = useRef<CanvasRenderingContext2D>();
    const operationContextRef = useRef<CanvasRenderingContext2D>();
    const statisticsContextRef = useRef<CanvasRenderingContext2D>();
    const originalXRef = useRef<d3.ScaleTime<number, number, never>>();
    const originalYRef = useRef<d3.ScaleLinear<number, number, never>>();
    const currentAxisRef = useRef<{
        x: d3.ScaleTime<number, number, never>,
        y: d3.ScaleLinear<number, number, never>
    }>();
    const margin = ({ top: 20, right: 30, bottom: 30, left: 50 });
    const [mouseXY, setMouseXY] = useState(['', 0]);
    const [changingAxis, setChangingAxis] = useState(0);
    const [loading, setLoading] = useState(false);

    const [currentX, setCurrentX] = useState(dataType === DATA_TYPE_VECTOR ? 1000 : 0);
    const [finalX, setFinalX] = useState(dataType === DATA_TYPE_VECTOR ? 1000 : 0);
    const [gpibChannelNames, setGpibChannelNames] = useState<string[]>([]);
    const [xType, setxType] = useState(2);
    const [gpibName, setGpibName] = useState('');
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (scatterData) {
            if (!serialNumberKeyword || serialNumberKeyword.length < 3) {
                setSearchedData([]);
                return;
            }

            const filteredData: [number, ParameteOverTime][] = []
            for (const d of scatterData) {
                const i = d.serial_number.indexOf(serialNumberKeyword);
                if (i !== -1) {
                    filteredData.push([i, d]);
                }
            }

            setSearchedData(filteredData.sort((a, b) => a[0] > b[0] ? 1 : (a[0] == b[0] ? 0 : -1)).map(v => v[1]).slice(0, 10));
        }
    }, [scatterData, serialNumberKeyword]);

    useEffect(() => {
        if (id.startsWith('lot_')) {
            setGpibChannelNames([]);
            setGpibName('');
            setxType(1);
            setIsReady(true);
        } else {
            setLoading(true);
            restRequest<string[]>(`testings/gpib_channel_names/${id}`, { measurement_key, channel: selectedChannel }, 'post').then(data => {
                setGpibChannelNames(data || []);
                if (!data || data.length === 0) {
                    setxType(1);
                } else {
                    if (!gpibName) {
                        setGpibName(data[0]);
                    }
                }
            }).finally(() => {
                setLoading(false);
                setIsReady(true);
            })
        }
    }, [id, dataType, measurement_name, export_file_name, selectedChannel])

    useEffect(() => {
        if (xType === 1) {
            setGpibName('');
        }
    }, [xType]);

    useEffect(() => {
        if (!isReady) {
            return;
        }

        setLoading(true);
        const params: any = { ...searchParams, measurement_key, channel: selectedChannel };
        if (dataType === DATA_TYPE_ARRAY) {
            if (gpibName) {
                params['gpib_name'] = gpibName
            } else {
                params['index'] = finalX
            }
        } else if (dataType === DATA_TYPE_VECTOR) {
            params['x'] = finalX
        }

        restRequest<ParameteOverTime[]>(`testings/overview_parameters_over_time/${id}`, params, 'post').then(data => {
            setScatterData(data.map(d => ({ ...d, date: moment(d.date as any, 'YYYY-MM-DD HH:mm').toDate() })));
        }).finally(() => setLoading(false));
    }, [id, dataType, measurement_name, export_file_name, selectedChannel, finalX, gpibName, isReady]);

    useLayoutEffect(() => {
        document.title = productName + ' ------ ' + measurement_name + ' Over Time Plot'
        const onResize = function () {
            if (svgRootRef.current) {
                const offset = $(svgRootRef.current).offset()!;
                const width = $(svgRootRef.current).innerWidth()!;

                setCanvasSize([width - 20, window.innerHeight - offset.top - 40]);
            }
        };
        $(window).on('resize', onResize);
        onResize();

        return () => {
            $(window).off('resize', onResize);
        }
    }, []);

    useEffect(() => {
        setDataPreparing(true);
        if (!scatterData) {
            return;
        }

        const fullWidth = canvasSize[0];
        const fullHeight = canvasSize[1];

        const width = fullWidth - margin.left - margin.right;
        const height = fullHeight - margin.top - margin.bottom;

        const xValues = scatterData.map(d => d.date);

        const x = d3.scaleTime().domain([xValues[0], _.last(xValues)] as Date[]).range([margin.left, width - margin.right]);
        const values = scatterData.map(d => d.value!)
        const median = d3.median(values)!
        const mean = d3.mean(values)!
        const std = d3.deviation(values)!
        setStatisticsData({ mean, median, std });
        setTotalNumber(values.length);
        const allValues = values.concat(scatterData.map(d => d.lower!).filter(v => !_.isUndefined(v) && !_.isNull(v))).concat(scatterData.map(d => d.upper!).filter(v => !_.isUndefined(v) && !_.isNull(v)));
        const yDomain = d3.extent(allValues) as number[];
        const y = d3.scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top]);

        originalXRef.current = x;
        originalYRef.current = y;

        currentAxisRef.current = {
            x,
            y
        }

        setDataPreparing(false);
    }, [scatterData]);

    useEffect(() => {
        if (dataPreparing) {
            return;
        }

        setCanvasPreparing(true);

        const [fullWidth, fullHeight] = canvasSize;
        let canvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.base');
        let operationCanvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.operation');
        let statisticsCanvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.statistics');

        if (canvas.size() === 0) {
            canvas = d3.select(svgRootRef.current!)
                .append("canvas")
                .attr('class', 'base');

            operationCanvas = d3.select(svgRootRef.current!)
                .append("canvas")
                .attr('class', 'operation')
                .style('background', 'rgba(255,255,255,0)')
                .style('position', 'absolute')
                .style('z-index', '1')
                .style('top', '0')
                .style('left', '0');
            statisticsCanvas = d3.select(svgRootRef.current!)
                .append("canvas")
                .attr('class', 'statistics')
                .style('background', 'rgba(255,255,255,0)')
                .style('position', 'absolute')
                .style('z-index', '0')
                .style('top', '0')
                .style('left', '0');
        }

        canvas.attr("width", fullWidth).attr("height", fullHeight);
        operationCanvas.attr("width", fullWidth).attr("height", fullHeight);
        statisticsCanvas.attr("width", fullWidth).attr("height", fullHeight);
        canvasContextRef.current = canvas.node()!.getContext('2d')!;
        operationContextRef.current = operationCanvas.node()!.getContext('2d')!;
        statisticsContextRef.current = statisticsCanvas.node()!.getContext('2d')!;

        setForSelectData([]);

        setCanvasPreparing(false);
    }, [scatterData, dataPreparing]);

    useEffect(() => {
        if (canvasPreparing) {
            return;
        }

        let isMounted = true;

        let operationCanvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.operation');
        let currentSelectedData: ParameteOverTime[] = [];

        const [fullWidth, fullHeight] = canvasSize;

        let miniX = currentAxisRef.current?.x ? Math.abs(currentAxisRef.current.x.invert(2).getTime() - currentAxisRef.current.x.invert(0).getTime()) : 2;
        let miniY = currentAxisRef.current?.y ? Math.abs(currentAxisRef.current.y.invert(2) - currentAxisRef.current.y.invert(0)) : 2;


        let isZooming = false;

        d3.select(document)
            .on('mousedown', function () {
                isZooming = true;
            }).on('mouseup', function () {
                isZooming = false;
            })

        operationCanvas.on('mouseover', function () {
        })
            .on('mouseout', function () {
            })
            .on('mousemove', function (event) {
                if (!isMounted || isZooming || event.button == 1) {
                    return;
                }

                if (scatterData) {
                    const mouse = d3.pointer(event);
                    const mouseX = currentAxisRef.current!.x.invert(mouse[0] - margin.left);
                    const mouseY = currentAxisRef.current!.y.invert(mouse[1] - margin.top);

                    const selectedUnits: [number, ParameteOverTime][] = [];

                    scatterData.forEach((d, index) => {
                        const xDiff = Math.abs(mouseX.getTime() - d.date.getTime());
                        if (xDiff < miniX) {
                            const yDiff = Math.abs(d.value - mouseY);
                            if (yDiff < miniY) {
                                selectedUnits.push([yDiff ** 2 + xDiff ** 2, d])
                            }
                        }
                    });

                    const selectedVectorData = selectedUnits.sort((a, b) => a[0] > b[0] ? 1 : (a[0] === b[0] ? 0 : -1)).slice(0, 20).map(s => s[1]);
                    setSelectedData(selectedVectorData);

                    currentSelectedData = selectedVectorData;
                }
            }).on('click', function (event) {
                setForSelectData(currentSelectedData);
                setForSelectSelectedData([]);

                const mouse = d3.pointer(event);
                const mouseX = currentAxisRef.current!.x.invert(mouse[0] - margin.left);
                const mouseY = currentAxisRef.current!.y.invert(mouse[1] - margin.top);
                setMouseXY([moment(mouseX).format('YYYY-MM-DD HH:mm'), mouseY]);
            });


        const width = fullWidth - margin.left - margin.right;
        const height = fullHeight - margin.top - margin.bottom;
        const zoom = d3.zoom()
            .scaleExtent([0.1, 10000])
            .translateExtent([[-width, -height], [width * 2, height * 2]])
            .on("zoom", (e: d3.D3ZoomEvent<any, any>) => {
                if (!isMounted) {
                    return;
                }
                currentAxisRef.current!.x = e.transform.rescaleX(originalXRef.current!);
                currentAxisRef.current!.y = e.transform.rescaleY(originalYRef.current!);
                setChangingAxis(s => s + 1);

                miniX = currentAxisRef.current?.x ? Math.abs(currentAxisRef.current.x.invert(2).getTime() - currentAxisRef.current.x.invert(0).getTime()) : 2;
                miniY = currentAxisRef.current?.y ? Math.abs(currentAxisRef.current.y.invert(2) - currentAxisRef.current.y.invert(0)) : 2;
            });

        operationCanvas.call(zoom as any);

        operationCanvas.on("dblclick.zoom", () => {
            if (!isMounted) {
                return;
            }

            currentAxisRef.current!.x = originalXRef.current!;
            currentAxisRef.current!.y = originalYRef.current!;
            operationCanvas.call(zoom.transform as any, d3.zoomIdentity.scale(1));
            operationCanvas.call(zoom.transform as any, d3.zoomIdentity.translate(0, 0));
            setChangingAxis(s => s + 1);

            const yDomain = currentAxisRef.current!.y.domain();
            const yRange = currentAxisRef.current!.y.range();
            miniY = yDomain && yRange ? Math.abs((yDomain[1] - yDomain[0]) * 2 / (yRange[1] - yRange[0])) : 2;
        });

        return () => {
            isMounted = false;
        }
    }, [canvasPreparing, canvasSize, scatterData]);

    useEffect(() => {
        if (dataPreparing || canvasPreparing || !scatterData) {
            return;
        }

        const currentAxis = currentAxisRef.current!;
        const operationContext = operationContextRef.current;
        if (operationContext) {
            operationContext.clearRect(0, 0, canvasSize[0], canvasSize[1]);
            operationContext.save();
            operationContext.translate(margin.left, margin.top);
            const currentX = currentAxis.x;
            const currentY = currentAxis.y;
            for (const d of _.difference(_.uniq(selectedData.concat(forSelectData)), forSelectSelectedData)) {
                operationContext.beginPath();
                operationContext.arc(currentX(d.date), currentY(d.value!), 3, 0, 2 * Math.PI, false);
                operationContext.fillStyle = "#FFBF00";

                operationContext.fill();
            }

            for (const d of forSelectSelectedData) {
                operationContext.beginPath();

                operationContext.arc(currentX(d.date), currentY(d.value!), 3, 0, 2 * Math.PI, false);
                operationContext.fillStyle = statusColors[d.status];
                operationContext.fill();
            }
            operationContext.restore();
        }
    }, [canvasSize, selectedData, forSelectSelectedData, dataPreparing, canvasPreparing, changingAxis]);

    useEffect(() => {
        if (dataPreparing || canvasPreparing || !scatterData) {
            return;
        }

        const [fullWidth, fullHeight] = canvasSize;
        const context = canvasContextRef.current!;
        context.clearRect(0, 0, fullWidth, fullHeight);

        const width = fullWidth - margin.left - margin.right;
        const height = fullHeight - margin.top - margin.bottom;

        const currentAxis = currentAxisRef.current!;

        const x = currentAxis.x;
        const y = currentAxis.y;

        const xDomain = x.domain();

        const xAxis = () => {
            const tickCount = 10;
            const tickSize = 12;
            const ticks = x.ticks(d3.timeDay.every(1) as any);
            const tickFormat = x.tickFormat(tickCount);

            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(x(d), height);
                context.lineTo(x(d), 0);
            });
            context.strokeStyle = "lightgray";
            context.stroke();

            const xAxisTitle = 'Date';
            context.beginPath();
            context.strokeStyle = "black";
            const metrics = context.measureText(xAxisTitle);
            const textWidth = metrics.width;
            context.fillText(xAxisTitle, x(xDomain[0]) + x(xDomain[1]) / 2 - x(xDomain[0]) / 2 - textWidth / 2, fullHeight - 20);

            context.beginPath();
            context.lineTo(0, height - tickSize);
            context.lineTo(width, height - tickSize);
            context.strokeStyle = "gray";
            context.stroke();

            context.textAlign = "center";
            context.textBaseline = "top";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), x(d), height + tickSize);
            });
        }

        const yAxis = () => {
            const tickCount = 10,
                tickSize = 6,
                tickPadding = 3,
                ticks = y.ticks(tickCount),
                tickFormat = y.tickFormat(tickCount);

            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(0, y(d));
                context.lineTo(width, y(d));
            });
            context.strokeStyle = "lightgray";
            context.stroke();

            context.beginPath();
            context.moveTo(-tickSize, 0);
            context.lineTo(0.5, 0);
            context.lineTo(0.5, height);
            context.lineTo(-tickSize, height);
            context.strokeStyle = "black";
            context.stroke();

            context.textAlign = "right";
            context.textBaseline = "middle";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), -tickSize - tickPadding, y(d));
            });

            const yAxisTitle = measurement_name;
            context.save();
            context.rotate(-Math.PI / 2);
            context.textAlign = "right";
            context.textBaseline = "top";
            context.strokeStyle = "black";
            const metrics = context.measureText(yAxisTitle);
            const textWidth = metrics.width;
            context.fillText(yAxisTitle, -(height / 2) + textWidth / 2, -margin.left);
            context.restore();
        }


        let tooltipTop = 0
        let currentTooltipElem = svgRootRef.current
        while (currentTooltipElem) {
            tooltipTop += currentTooltipElem.offsetTop;
            currentTooltipElem = currentTooltipElem.offsetParent as any;
        }

        context.save();
        context.translate(margin.left, margin.top);
        xAxis();
        yAxis();

        scatterData.forEach((d, index) => {
            context.beginPath();
            context.arc(x(d.date), y(d.value!), 3, 0, 2 * Math.PI, false);

            context.strokeStyle = statusColors[d.status];
            context.lineWidth = 1;
            context.stroke();
        });

        scatterData.filter(d => !_.isUndefined(d.lower) && !_.isNull(d.lower)).forEach((d, index) => {
            context.beginPath();

            context.arc(x(d.date), y(d.lower!), 2, 0, 2 * Math.PI, false);

            context.strokeStyle = 'darkred';
            context.lineWidth = 1;
            context.stroke();
        });

        scatterData.filter(d => !_.isUndefined(d.upper) && !_.isNull(d.upper)).forEach((d, index) => {
            context.beginPath();

            context.arc(x(d.date), y(d.upper!), 2, 0, 2 * Math.PI, false);

            context.strokeStyle = 'darkred';
            context.lineWidth = 1;
            context.stroke();
        });

        context.restore();
    }, [scatterData, dataPreparing, canvasPreparing, changingAxis]);


    useEffect(() => {
        if (dataPreparing || canvasPreparing || !statisticsData) {
            return;
        }

        const [fullWidth, fullHeight] = canvasSize;
        const context = statisticsContextRef.current!;
        context.clearRect(0, 0, fullWidth, fullHeight);
        const width = fullWidth - margin.left - margin.right;

        const currentAxis = currentAxisRef.current!;

        const x = currentAxis.x;
        const y = currentAxis.y;

        context.save();
        context.translate(margin.left, margin.top);

        if (selectMean) {
            context.beginPath();
            context.moveTo(0, y(statisticsData.mean));
            context.lineTo(width, y(statisticsData.mean));
            context.strokeStyle = '#21ba45';
            context.lineWidth = 1;
            context.stroke();
        }

        if (selectMedian) {
            context.beginPath();
            context.moveTo(0, y(statisticsData.median));
            context.lineTo(width, y(statisticsData.median));
            context.strokeStyle = '#009c95';
            context.lineWidth = 1;
            context.stroke();
        }

        if (selectSigma3Limits) {
            const up = y(statisticsData.mean + 3 * statisticsData.std);
            const down = y(statisticsData.mean - 3 * statisticsData.std);
            context.beginPath();
            context.moveTo(0, up);
            context.lineTo(width, up);
            context.strokeStyle = '#e10f85';
            context.lineWidth = 1;
            context.stroke();

            context.beginPath();
            context.moveTo(0, down);
            context.lineTo(width, down);
            context.strokeStyle = '#e10f85';
            context.lineWidth = 1;
            context.stroke();
        }

        context.restore();

    }, [dataPreparing, canvasPreparing, statisticsData, selectMean, selectMedian, selectSigma3Limits, changingAxis])

    return <div style={{ padding: '0 1rem' }}>
        <Header attached="top">{productName} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {measurement_name} <sub>{export_file_name}</sub>
            <Button compact size="small" floated="right" onClick={e => {
                e.preventDefault();

                const params: any = { ...searchParams, measurement_key, channel: selectedChannel };
                if (dataType === DATA_TYPE_ARRAY) {
                    params['index'] = finalX
                } else if (dataType === DATA_TYPE_VECTOR) {
                    params['x'] = finalX
                }

                window.open(hostAbsoluteUrl('testings/overview_parameters_over_time/export_as_pkl/' + id + '?no_cache=' + Date.now() + '&searchData=' + toUrlQueryString(params)))
            }}>Export PKL</Button>
        </Header>
        <Segment attached="bottom" compact basic style={{ padding: '0', margin: '0' }} loading={loading}>
            <Segment.Group horizontal={true} compact style={{ padding: '2px', margin: '0', border: 'none' }}>
                <Segment style={{ width: '250px', flex: 'inherit' }}>
                    {maxChannel > 1 && <>
                        <Card>
                            <Card.Header textAlign="center">Channels</Card.Header>
                            <Card.Content>
                                <Button.Group compact>
                                    {_.range(1, maxChannel + 1).map(c => <Button key={c} secondary basic={selectedChannel !== c} onClick={e => {
                                        setSelectedChannel(c);
                                    }}>CH{c}</Button>)}
                                </Button.Group>
                            </Card.Content>
                        </Card>
                        <Divider></Divider>
                    </>}


                    {dataType !== DATA_TYPE_SCALAR && <>
                        {gpibChannelNames && gpibChannelNames.length > 0 && <Select value={xType} onChange={(e, d) => setxType(d.value as number)} options={[{ key: 1, value: 1, text: 'By Index' }, { key: 2, value: 2, text: 'By GPIB Channel Name' }]} fluid></Select>}

                        {!(xType === 2 && gpibChannelNames.length > 0) && <>
                            <Input action={dataType === DATA_TYPE_ARRAY ? 'index' : 'x'} actionPosition="left" fluid input={<input value={currentX} onChange={(e) => {
                                try {
                                    const val = dataType === DATA_TYPE_ARRAY ? parseInt(e.target.value as string) : parseFloat(e.target.value as string);
                                    if (!isNaN(val)) {
                                        setCurrentX(val);
                                    }
                                } catch (e) { }
                            }} onBlur={() => {
                                setFinalX(currentX);
                            }} min={0} />}>
                            </Input>
                            <Divider inverted />
                            {dataType === DATA_TYPE_VECTOR && <Button size="mini" onClick={e => {
                                e.preventDefault();
                                setCurrentX(-1);
                                setFinalX(-1);
                            }}>Max Abs</Button>}
                            <Divider></Divider>
                        </>}

                        {xType === 2 && gpibChannelNames.length > 0 && <Dropdown value={gpibName} onChange={(e, d) => setGpibName(d.value as string)} selection search options={gpibChannelNames.map(v => {
                            const vs = v.split('#', 2)
                            return { key: v, value: v, text: vs[1].toLocaleLowerCase() != 'none' ? vs[1] : `Port ${vs[0]}` };
                        })} fluid></Dropdown>}
                    </>}

                    <Header attached="top" size="tiny">Selected  <div style={{ float: 'right', fontWeight: 'lighter' }}>Total: {totalNumber}</div></Header>
                    <Segment attached="bottom" size="tiny">
                        {forSelectData.length === 1 ? <div style={{ color: statusColors[forSelectData[0].status] }}>
                            X: {formatDateTime(forSelectData[0].date)}<br />
                            Y: {forSelectData[0].value}
                        </div>
                            :
                            <div>
                                X: {mouseXY[0]}<br />
                                Y: {mouseXY[1]}
                            </div>}

                        <Divider></Divider>
                        <div className="button_container">
                            {forSelectData.map(d => <Popup
                                key={d.file_id}
                                on="focus"
                                header={d.serial_number}
                                content={<div className="ui list">
                                    <div className="ui item"><b>Date Time:</b> {formatDateTime(d.date)}</div>
                                    <div className="ui item"><b>Value:</b> {d.value}</div>
                                    <div className="ui item"><b>Lower:</b> {d.lower}</div>
                                    <div className="ui item"><b>Upper:</b> {d.upper}</div>
                                    <div className="ui item"><b>File:</b> {_.last(d.file_path.split('/'))}</div>

                                    <Button size="mini" compact color="teal" onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        window.open(`/testing_file_visual/${d.file_id}/3/${measurement_key}`, '_blank');
                                    }}>Detail</Button>
                                </div>}

                                trigger={<Label style={{ opacity: forSelectSelectedData.includes(d) ? 1 : 0.314, background: statusColors[d.status], color: '#fff', cursor: 'pointer' }} onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (forSelectSelectedData.includes(d)) {
                                        setForSelectSelectedData(vs => vs.filter(v => v !== d));
                                    } else {
                                        setForSelectSelectedData(vs => [...vs, d]);
                                    }
                                }}
                                    title="Click me to see more"
                                    size="mini" compact>{d.serial_number}</Label>}></Popup>)}
                        </div>
                    </Segment>

                    <Divider></Divider>

                    <Header attached="top">
                        <Input icon placeholder='Search By Serial...' size="small">
                            <input value={serialNumberKeyword} onChange={e => setSerialNumberKeyword(e.target.value)} />
                            <Icon name='search' />
                        </Input>
                    </Header>

                    <Card>
                        <Card.Header textAlign="center">Channels</Card.Header>
                        <Card.Content>
                            <Button.Group compact vertical fluid>
                                <Button color="green" basic={!selectMean} onClick={e => {
                                    setSelectMean(v => !v);
                                }}>Mean</Button>
                                <Button color="teal" basic={!selectMedian} onClick={e => {
                                    setSelectMedian(v => !v);
                                }}>Median</Button>
                                <Button color="pink" basic={!selectSigma3Limits} onClick={e => {
                                    setSelectSigma3Limits(v => !v);
                                }}>Sigma +/- 3</Button>
                            </Button.Group>
                        </Card.Content>
                    </Card>
                    <Divider></Divider>

                    <Segment attached="bottom" size="tiny">
                        <div className="button_container">
                            {searchedData.map(d => <Button key={d.file_id} onClick={e => {
                                e.preventDefault();

                                let contains = false;
                                if (forSelectSelectedData.includes(d)) {
                                    setForSelectSelectedData(vs => vs.filter(v => v !== d));
                                    contains = true;
                                }

                                if (selectedData.includes(d)) {
                                    setSelectedData(vs => vs.filter(v => v !== d));
                                    contains = true;
                                }

                                if (!contains) {
                                    setForSelectSelectedData(vs => [...vs, d]);
                                }

                            }} onDoubleClick={(e: any) => {
                                e.preventDefault();
                                window.open(`/testing_file_visual/${d.file_id}/3/${measurement_key}`, '_blank');
                            }} style={{ opacity: (forSelectSelectedData.includes(d) || selectedData.includes(d)) ? 1 : 0.314, background: statusColors[d.status], color: '#fff' }} title={`file: ${_.last(d.file_path.split('/'))}, value: ${d.value}, lower: ${d.lower}, upper: ${d.upper} (Double Click to Detail)`} size="mini" compact>{d.serial_number}</Button>)}
                        </div>
                    </Segment>
                </Segment>
                <Segment>
                    <div style={{ float: 'right', position: 'relative', width: `100%` }} ref={svgRootRef}></div>
                </Segment>
            </Segment.Group>
        </Segment>
    </div>
}
